import { useNavigate, useParams } from "react-router";
import { BreadcrumbItem } from "../../generated-api/device";
import { getBreadcrumbLink } from "./Breadcrumb.helpers";
import {
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbSeparator,
  NextButton,
  PreviousButton,
  Wrapper,
} from "./Breadcrumb.styles";
import BreadCrumbLoader from "./Breadcrumb.loader";
import { useEffect, useRef, useState } from "react";
import {
  ArrowIndicatorRightOutlined,
  ArrowNavigationLeftOutlined,
  ArrowNavigationRightOutlined,
} from "@gds-react/icons";
import { useWindowResize } from "../../hooks/useWindowResize";
import { DeviceTabTypeEnum } from "../../types/DeviceTabTypeEnum";

interface Props {
  list?: BreadcrumbItem[];
  isLoading?: boolean;
}

export default function BreadCrumb({ list, isLoading = true }: Props) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [showRightButton, setShowRightButton] = useState(false);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const { accountId, installationId, tabIndex } = useParams();
  const navigate = useNavigate();
  const { width: windowWidth } = useWindowResize();

  useEffect(() => {
    if (wrapperRef.current) {
      const event = {
        currentTarget: wrapperRef.current,
      } as React.UIEvent<HTMLDivElement>;
      onScroll(event);
    }
  }, [list, isLoading, windowWidth]);

  const onScroll = (e) => {
    const elm = e.currentTarget as HTMLDivElement;

    if (!elm) return;
    const showLeft = elm.scrollLeft !== 0;
    const showRight = elm.scrollLeft + elm.clientWidth < elm.scrollWidth;
    setShowLeftButton(showLeft);
    setShowRightButton(showRight);
  };

  const next = () => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollBy({ left: 50, behavior: "smooth" });
    }
  };

  const previous = () => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollBy({ left: -50, behavior: "smooth" });
    }
  };

  return isLoading ? (
    <BreadCrumbLoader />
  ) : (
    <Wrapper
      $showRightButton={showRightButton}
      $showLeftButton={showLeftButton}
    >
      <Breadcrumb onScroll={onScroll} ref={wrapperRef}>
        {list &&
          list.map((item, index) => {
            const link = getBreadcrumbLink(item, {
              accountId: accountId ?? "",
              installationId: installationId ?? "",
              deviceTab: tabIndex as DeviceTabTypeEnum,
            });
            return (
              <BreadcrumbLink
                key={index}
                onClick={() => {
                  if (link) navigate(link);
                }}
                $active={index === list.length - 1}
                $disabled={!link}
                style={{ scrollSnapAlign: "start" }}
              >
                {item.name}
                {index !== list.length - 1 && (
                  <BreadcrumbSeparator>
                    <ArrowIndicatorRightOutlined />
                  </BreadcrumbSeparator>
                )}
              </BreadcrumbLink>
            );
          })}
      </Breadcrumb>

      {showRightButton && (
        <NextButton>
          <ArrowNavigationRightOutlined
            style={{ width: 16, height: 16 }}
            onClick={next}
          />
        </NextButton>
      )}
      {showLeftButton && (
        <PreviousButton>
          <ArrowNavigationLeftOutlined
            style={{ width: 16, height: 16 }}
            onClick={previous}
          />
        </PreviousButton>
      )}
    </Wrapper>
  );
}
